<template>
  <div>
    <v-skeleton-loader
      v-if="isInitializing"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else-if="hasPermission('sites.view') && hasPermission('reports.view')"
      :headers="tableHeaders"
      :items="displayedReports"
      :pagination="pagination"
      :loading="loading"
      :isSearchable="false"
      :title="$t('sitePage.reports')"
      :isInfinitScroll="true"
      :centerColumns="['action']"
      :notSortableColumns="['action']"
      :dataCy="dataCy.sites.reports.list"
      hideDefaultFooter
      @iconAction="downloadReport"
      @sendQueryParams="fetchQuery"
    >
      <template #SearchWithFilters>
        <SearchWithFilters
          :searchPlaceholder="$t('generic.search')"
          :filters="filters"
          :filterName="filterName"
          @onApplyFilters="handleApplyFilters"
          @onSearch="handleSearch"
        />
      </template>
      <template #item.action="{item}">
        <v-menu offsetY>
          <template v-slot:activator="{ on, attrs }">
            <div class="action-buttons">
              <v-btn
                color="primary"
                fab
                xSmall
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>
                  mdi-dots-vertical
                </v-icon>
              </v-btn>
            </div>
          </template>

          <v-list>
            <ListItem
              class="report"
              icon="mdi-file-pdf-box"
              title="PDF"
              @click="getCurrentPDF(item)"
            />

            <ListItemMenu
              class="history"
              icon="mdi-history"
              :title="$t('sites.versionsHistory')"
              :items="reportsHistory"
              :dataCy="dataCy.modals.listItemMenu.card"
              @click="getReportsHistory(item)"
              @selected-option="downloadReport"
            />
            <div
              class="v-list-item v-list-item--link theme--light"
              :class="{ 'disabled-history': !hasHistory(item.index) }"
              @click="hasHistory(item.index) ? showHistoryData(item.index) : null"
            >
              <v-icon>mdi-archive</v-icon>
              <span class="text-adjust">{{ $t('labReports.labels.history') }}</span>
            </div>
          </v-list>
        </v-menu>
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </TableRemote>
    <NoPermissionAlert
      v-else
      :message="$t('permissions.error')"
    />
    <LoadingWidget
      :show="showLoadingWidget"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ListItem from '@/components/shared/widgets/ListItem';
import ListItemMenu from '@/components/shared/widgets/ListItemMenu';
import LoadingWidget from '@/components/shared/widgets/LoadingWidget';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import NoPermissionAlert from '@/components/shared/NoPermissionAlert';
import { isEmpty } from 'lodash';
import moment from 'moment';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import { sites, modals } from '@/aq-data-cy.json';
import perpage from '@/constants/pagination';
import HistoryModal from '@/components/shared/HistoryModal.vue';

export default {
    components: {
        LoadingWidget,
        TableRemote,
        NoPermissionAlert,
        ListItem,
        ListItemMenu,
        SearchWithFilters,
    },
    data() {
        return {
            loading: false,
            isInitializing: true,
            showLoadingWidget: false,
            reports: [],
            reportsHistory: [],
            pagination: {},
            filterName: 'siteReports',
            queryParams: {
                perpage,
                startDate: moment().subtract(30, 'days').startOf('day').toISOString(),
                endDate: moment().endOf('day').toISOString(),
            },
            filters: [
                {
                    key: 'date',
                    label: 'laboratory.labels.date',
                    type: 'date',
                    value: [moment().subtract(30, 'days').startOf('day').toISOString(), moment().endOf('day').toISOString()],
                    dateKeys: ['startDate', 'endDate'],
                    isRange: true,
                    datacy: 'LabReportDate',
                },
            ],
        };
    },
    computed: {
        ...mapGetters([
            'lang',
            'site',
            'hasPermission',
        ]),
        tableHeaders() {
            const headers = [
                'name',
                'woNumber',
                'type',
                'author',
                'date',
            ].map(value => ({
                sortable: false,
                text: this.$t(`siteReports.labels.${value}`),
                value,
            }));
            headers.push({
                text: this.$t('controls.actions'),
                value: 'action',
                sortable: false,
            });
            return headers;
        },

        displayedReports() {
            return this.reports.map((report, index) => {
                let date;
                if (report.labWorkOrder) date = report.createdAt;
                else if (report.workOrder.startDate) date = report.workOrder.startDate;
                else date = [];

                return {
                    ...report,
                    index,
                    name: report.name,
                    author: `${report.createdBy.firstName} ${report.createdBy.lastName}`,
                    type: report.type ? report.type.label : '',
                    date: date && this.$options.filters.formatDate(date, ('L LT')),
                    woNumber: report.workOrder?.woNumber || report.labWorkOrder?.woNumber,
                };
            });
        },

        hasSiteId() {
            return Boolean(this.site.id);
        },
        dataCy() {
            return {
                sites,
                modals,
            };
        },
    },

    watch: {
        async lang() {
            this.pagination = {};
            this.reports = [];
            await this.getSiteReports();
        },
    },

    async created() {
        // Do not fetch unless a site id is present:
        if (!this.hasSiteId) return;

        await this.getSiteReports();

        this.isInitializing = false;
    },

    methods: {
        hasHistory(index) {
            const report = this.reports[index];
            return report && report.labWorkOrder && report.labWorkOrder.history && report.labWorkOrder.history.length > 0;
        },
        showHistoryData(index) {
            const report = this.reports[index];

            const historyItems = report.labWorkOrder.history.map(historyEntry => ({
                date: this.$options.filters.formatDate(historyEntry.createdAt, 'L LT'),
                details: historyEntry.manipulatedDetails,
                action: historyEntry.action,
            }));

            this.$root.$emit('showModal', {
                title: this.$t('siteReports.labels.history'),
                component: HistoryModal,
                payload: { itemsForHistory: historyItems },
                showConfirmButton: false,

            });
        },
        async getSiteReports(overWrite = false) {
            this.loading = true;
            // for first time fetch Or when the requested page never fetched
            if (isEmpty(this.pagination) || (this.pagination?.currentPage !== this.queryParams?.page)) {
                if (overWrite) {
                    this.queryParams.page = 1;
                }
                const { data } = await this.$rq.getSiteReports(this.site.id, this.queryParams);
                const reports = data.reports.filter(report => report.workOrder || report.labWorkOrder);
                this.reports = overWrite ? [...reports] : [...this.reports, ...reports];
                this.pagination = data.pagination;
            }
            this.loading = false;
        },

        async fetchQuery(queryParams) {
            this.queryParams = {
                ...queryParams,
                page: 1,
            };
            this.pagination = {};
            this.reports = [];
            await this.getSiteReports();
        },

        async downloadReport({ file }) {
            let { id, fileName } = file;
            // eslint-disable-next-line no-underscore-dangle
            if (file._id || file.id) {
                // eslint-disable-next-line no-underscore-dangle
                id = file._id || file.id;
                fileName = file.fileName;
            }
            this.showLoadingWidget = true;
            const { data } = await this.$rq.downloadFile(id);
            this.$utils.saveFileOnDisk(data, fileName);
            this.showLoadingWidget = false;
        },

        async getReportsHistory(report) {
            if (report.labWorkOrder) {
                await this.getLabReportsHistory(report);
            } else if (report.workOrder) {
                await this.getWorkOrderReportHistory(report);
            }
        },
        async getWorkOrderReportHistory(report) {
            const response = await this.$rq.getWorkOrder(report.workOrder.id);
            this.reportsHistory = response.data.reportsHistory.map(entry => ({
                ...entry, name: entry.file.fileName,
            }));
        },
        async getLabReportsHistory(report) {
            const { data } = await this.$rq.getLabWorkOrder(report.labWorkOrder.id);

            this.reportsHistory = data.reports.map(entry => ({
                ...entry, name: entry.file.fileName,
            }));
        },
        async getCurrentPDF(report) {
            let data;
            this.showLoadingWidget = true;
            if (report.labWorkOrder) {
                ({ data } = await this.$rq.getLabWorkOrder(report.labWorkOrder.id));
            } else if (report.workOrder) {
                ({ data } = await this.$rq.getWorkOrder(report.workOrder.id));
            }

            if (data) {
                await this.downloadReport(data.lastReport);
            }
            this.showLoadingWidget = false;
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getSiteReports();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
        handleSearch(search) {
            this.queryParams = {
                ...this.queryParams,
                search,
            };
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            this.queryParams = cleanQueryParams;
            this.pagination = {};
            this.getSiteReports(true);
        },
        handleApplyFilters(filterParams) {
            if (filterParams?.reset) {
                this.clearFilters();
            } else {
                this.queryParams = {
                    ...this.queryParams,
                    ...filterParams,
                    page: 1,
                };
                const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
                this.queryParams = cleanQueryParams;
                this.pagination = {};
            }
            this.getSiteReports(true);
        },
        clearFilters() {
            this.queryParams = {
                perpage,
                startDate: moment().subtract(30, 'days').startOf('day').toISOString(),
                endDate: moment().endOf('day').toISOString(),
                search: '',
                page: 1,
            };
            this.pagination = {};
            this.getSiteReports(true);
        },
    },
};
</script>

<style lang="scss" scoped>
.text-adjust {
  flex-grow: 1;
  font-size: 16px;
}
.disabled-history {
  pointer-events: none;
  color: grey;
  opacity: 0.5;}
</style>;
